import {
    Button as MuiButton,
    IconButton as MuiIconButton,
    styled,
} from "@mui/material"

const variantsConfig = (theme) => ({
    primary: {
        backgroundColor: theme.palette.primary.main,
        textColor: theme.palette.primary.contrastText,
        hoverBackgroundColor: theme.palette.primary.dark,
        hoverTextColor: theme.palette.primary.contrastText,
        focusBackgroundColor: theme.palette.primary.light,
        focusTextColor: theme.palette.primary.contrastText,
        disabledBackgroundColor: theme.palette["action/disabledBackground"].main,
        disabledTextColor: theme.palette["action/disabled"].main,
    },
    secondary: {
        backgroundColor: theme.palette.secondary.main,
        textColor: theme.palette.secondary.contrastText,
        hoverBackgroundColor: theme.palette.secondary.dark,
        hoverTextColor: theme.palette.secondary.contrastText,
        focusBackgroundColor: theme.palette.secondary.light,
        focusTextColor: theme.palette.secondary.contrastText,
        disabledBackgroundColor: theme.palette["action/disabledBackground"].main,
        disabledTextColor: theme.palette["action/disabled"].main,
    },
    tertiary: {
        backgroundColor: theme.palette["common/white_states"].main,
        textColor: theme.palette.primary.main,
        hoverBackgroundColor: theme.palette.secondary.main,
        hoverTextColor: theme.palette.primary.main,
        focusBackgroundColor: theme.palette.secondary.dark,
        focusTextColor: theme.palette.primary.main,
        disabledBackgroundColor: theme.palette["action/disabledBackground"].main,
        disabledTextColor: theme.palette["action/disabled"].main,
    }
})

const sizesConfig = (theme) => ({
    small: {
        height: 32,
        padding: 12,
        typography: theme.typography["Desktop & Mobile/Body/Small"],
    },
    medium: {
        height: 40,
        padding: 16,
        typography: theme.typography["Desktop & Mobile/Body/Regular"],
    },
    large: {
        height: 48,
        padding: 16,
        typography: theme.typography["Desktop & Mobile/Body/Regular 18"],
    }
})

type StyledButtonProps = {
    buttonVariant: "primary" | "secondary" | "tertiary" | "text" | "dark"
    buttonSize: "small" | "medium" | "large"
    outlined?: boolean
}

export const Button = styled(MuiButton, { shouldForwardProp: prop => !["buttonVariant", "buttonSize", "outlined"].includes(prop.toString()) })<StyledButtonProps>
    (({ theme, buttonVariant = "primary", buttonSize = "medium" }) => {
        const variantConfig = variantsConfig(theme)[buttonVariant]
        const sizeConfig = sizesConfig(theme)[buttonSize]

        return {
            "&.MuiButton-root": {
                backgroundColor: variantConfig?.backgroundColor,
                borderRadius: 9999,
                color: variantConfig?.textColor,
                height: sizeConfig?.height,
                padding: sizeConfig?.padding,
                textTransform: "none",

                fontFamily: sizeConfig.typography.fontFamily,
                fontSize: sizeConfig.typography.fontSize,
                fontStyle: sizeConfig.typography.fontStyle,
                fontWeight: sizeConfig.typography.fontWeight,
                lineHeight: sizeConfig.typography.lineHeight,

                "&:disabled": {
                    backgroundColor: variantConfig?.disabledBackgroundColor,
                    color: variantConfig?.disabledTextColor,
                },
                "&.MuiButton-loading": {
                    color: "transparent",

                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                        opacity: 0,
                    },
                }
            },
            "@media (hover: hover)": {
                "&.MuiButton-root:hover": {
                    backgroundColor: variantConfig?.hoverBackgroundColor,
                    color: variantConfig?.hoverTextColor,
                },
                "&.Mui-focusVisible": {
                    backgroundColor: variantConfig?.hoverBackgroundColor,
                    color: variantConfig?.hoverTextColor,
                },
            },
        }
    })

type StyledIconButtonProps = {
    buttonVariant: "primary" | "secondary" | "tertiary" | "text" | "dark"
    buttonSize: "small" | "medium" | "large"
    buttonShape: "square" | "circle"
    outlined?: boolean
}

export const IconButton = styled(MuiIconButton, { shouldForwardProp: prop => !["buttonVariant", "buttonSize", "buttonShape", "outlined"].includes(prop.toString()) })<StyledIconButtonProps>
    (({ theme, buttonVariant = "primary", buttonSize = "medium", buttonShape = "circle", outlined = true }) => {
        const variantConfig = variantsConfig(theme)[buttonVariant]
        const sizeConfig = sizesConfig(theme)[buttonSize]

        return {
            "&.MuiIconButton-root": {
                borderRadius: buttonShape === "circle" ? 9999 : 12,
                border: (buttonVariant === "tertiary" && outlined) ? `1px solid ${variantConfig?.textColor}` : "none",
                width: sizeConfig?.height,
                height: sizeConfig?.height,
                backgroundColor: variantConfig?.backgroundColor,
                color: variantConfig?.textColor,

                "&:disabled": {
                    border: (buttonVariant === "tertiary" && outlined) ? `1px solid ${variantConfig?.disabledTextColor}` : "none",
                    backgroundColor: variantConfig?.disabledBackgroundColor,
                    color: variantConfig?.disabledTextColor,
                }
            },
            "@media (hover: hover)": {
                "&.MuiIconButton-root:hover": {
                    backgroundColor: variantConfig?.hoverBackgroundColor,
                    color: variantConfig?.hoverTextColor,
                },
                "&.Mui-focusVisible": {
                    border: (buttonVariant === "tertiary" && outlined) ? `1px solid ${variantConfig?.hoverTextColor}` : "none",
                    backgroundColor: variantConfig?.hoverBackgroundColor,
                    color: variantConfig?.hoverTextColor,
                },
            },
        }
    })

type StyledTextButtonProps = {
    buttonColor?: string
    buttonSize: "small" | "medium" | "large"
}

export const TextButton = styled("button")<StyledTextButtonProps>
    (({ theme, buttonColor = "", buttonSize = "medium", }) => {
        const sizeConfig = sizesConfig(theme)[buttonSize]

        return {
            backgroundColor: "transparent",
            border: "none",
            padding: theme.spacing(2, 0),
            width: "fit-content",
            color: theme.palette[buttonColor]?.main || theme.palette.primary.main,
            fontFamily: sizeConfig.typography.fontFamily,
            fontSize: sizeConfig.typography.fontSize,
            fontStyle: sizeConfig.typography.fontStyle,
            fontWeight: sizeConfig.typography.fontWeight,
            lineHeight: sizeConfig.typography.lineHeight,

            "&:not([disabled]):hover": {
                color: theme.palette[buttonColor]?.dark || theme.palette.primary.dark,
                fontWeight: 600,

                "& .TextButton-underline": {
                    backgroundColor: theme.palette[buttonColor]?.dark || theme.palette.primary.dark,
                }
            },

            "&:disabled": {
                color: theme.palette["action/disabled"].main,

                "& .TextButton-underline": {
                    backgroundColor: theme.palette["action/disabled"].main,
                }
            }

        }
    })

type StyledTextUnderlineProps = {
    buttonColor?: string
}

export const Underline = styled("span")<StyledTextUnderlineProps>(({ theme, buttonColor = "" }) => ({
    display: "block",
    width: "100%",
    height: 1,
    backgroundColor: theme.palette[buttonColor]?.main || theme.palette.primary.main,
    marginTop: theme.spacing(0.5),
}))